/* eslint-disable react-refresh/only-export-components --
   Context module: NotificationProvider, the useNotify hook, and the module-level
   notify() all share the globalNotifyImpl / NotificationContext state defined in
   this file. Splitting the hook and function out for a dev-HMR benefit fragments
   the provider from its own accessors. */
import {
  Alert,
  AlertColor,
  AlertTitle,
  Snackbar,
  SnackbarCloseReason,
} from "@mui/material";
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

/**
 * Input accepted by the notification APIs.
 *
 * Consumers can provide either a plain string message (via {@link notify} or
 * {@link useNotify}) or a full object with severity/title/options.
 */
type NotificationInput = {
  /** Visual style for the snackbar alert. Defaults to `"info"`. */
  severity?: AlertColor;
  /** Optional heading rendered above the message body. */
  title?: string;
  /** Main notification text shown to users. */
  message: string;
  /**
   * Duration (ms) before the snackbar auto-closes.
   * Falls back to 4500 when omitted.
   */
  autoHideDuration?: number;
};

/**
 * Internal queue item with a monotonically increasing identifier.
 */
type QueuedNotification = NotificationInput & {
  id: number;
};

/**
 * Function signature exposed by the notification context and imperative bridge.
 */
type NotifyFn = (notification: NotificationInput | string) => void;

const NotificationContext = createContext<NotifyFn | undefined>(undefined);

let globalNotifyImpl: NotifyFn | null = null;
let nextNotificationId = 1;

/**
 * Converts string notifications into a normalized object shape.
 */
function normalizeNotification(
  notification: NotificationInput | string,
): NotificationInput {
  if (typeof notification === "string") {
    return {
      message: notification,
      severity: "info",
    };
  }

  return {
    severity: "info",
    ...notification,
  };
}

/**
 * Imperative notification entry-point for non-React code paths.
 *
 * Typical usage is from utility modules, model adapters, or legacy code that
 * cannot call React hooks directly.
 */
export function notify(notification: NotificationInput | string): void {
  if (!globalNotifyImpl) {
    console.warn(
      "notify called before NotificationProvider mounted",
      notification,
    );
    return;
  }

  globalNotifyImpl(notification);
}

/**
 * Returns the app-scoped notification publisher.
 *
 * Must be used within {@link NotificationProvider}; otherwise it throws.
 */
export function useNotify(): NotifyFn {
  const ctx = useContext(NotificationContext);

  if (!ctx) {
    throw new Error("useNotify must be used inside NotificationProvider");
  }

  return ctx;
}

/**
 * Provides a global snackbar notification system.
 *
 * Behavior:
 * - Notifications are queued in FIFO order.
 * - One snackbar is shown at a time.
 * - Dismissal/timeout advances the queue.
 * - An imperative `notify(...)` bridge is registered while mounted.
 */
export const NotificationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [queue, setQueue] = useState<QueuedNotification[]>([]);
  const [current, setCurrent] = useState<QueuedNotification | null>(null);

  const enqueue = useCallback<NotifyFn>((notification) => {
    const normalized = normalizeNotification(notification);

    setQueue((prev) => [
      ...prev,
      {
        id: nextNotificationId++,
        ...normalized,
      },
    ]);
  }, []);

  useEffect(() => {
    globalNotifyImpl = enqueue;

    return () => {
      if (globalNotifyImpl === enqueue) {
        globalNotifyImpl = null;
      }
    };
  }, [enqueue]);

  useEffect(() => {
    if (current || queue.length === 0) {
      return;
    }

    setCurrent(queue[0]);
    setQueue((prev) => prev.slice(1));
  }, [current, queue]);

  const handleClose = useCallback(
    (_event?: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
      if (reason === "clickaway") {
        return;
      }

      setCurrent(null);
    },
    [],
  );

  const contextValue = useMemo(() => enqueue, [enqueue]);

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      {current ? (
        <Snackbar
          open
          onClose={handleClose}
          autoHideDuration={current.autoHideDuration ?? 4500}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <Alert
            onClose={handleClose}
            severity={current.severity ?? "info"}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {current.title ? <AlertTitle>{current.title}</AlertTitle> : null}
            {current.message}
          </Alert>
        </Snackbar>
      ) : null}
    </NotificationContext.Provider>
  );
};
